package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.PayrollRecordDto
import se.mittpass.shared.api.UpdatePayrollRecordRequest

private val weekScope = MainScope()

@Composable
internal fun PayrollWeekView(
    weekStart: String,
    records: List<PayrollRecordDto>,
    drag: BarDragState?,
    onDragStart: (BarDragState) -> Unit,
    onDragUpdate: (BarDragState) -> Unit,
    onDragEnd: (BarDragState) -> Unit,
    onEditRecord: (PayrollRecordDto) -> Unit,
    onReload: () -> Unit,
    isMobile: Boolean = false
) {
    // Group by person, same as month view
    val byPerson = remember(records) {
        records.groupBy { it.userId }
            .entries
            .sortedBy { (_, recs) ->
                listOfNotNull(recs.first().firstName, recs.first().lastName).joinToString(" ")
                    .ifEmpty { recs.first().userEmail }
            }
    }

    val autoExpand = byPerson.size <= 4
    val expandedIds = remember { mutableStateMapOf<Long, Boolean>() }
    // Initialize newly seen users without wiping existing expanded states
    LaunchedEffect(byPerson) {
        byPerson.forEach { (uid, _) ->
            if (!expandedIds.containsKey(uid)) {
                expandedIds[uid] = autoExpand
            }
        }
    }

    // Legend shown once
    PayrollBarLegend()

    val allExpanded = byPerson.all { (uid, _) -> expandedIds[uid] == true }

    Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("margin-bottom", "0.25rem") } }) {
        Button({
            onClick {
                val target = !allExpanded
                byPerson.forEach { (uid, _) -> expandedIds[uid] = target }
            }
            style {
                property("background", "transparent"); property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "8px"); property("padding", "0.35rem 0.75rem")
                property("cursor", "pointer"); property("font-size", "0.78rem")
                property("font-weight", "600"); property("color", "var(--rp-subtle)")
                property("font-family", "inherit"); property("display", "flex")
                property("align-items", "center"); property("gap", "0.35rem")
            }
        }) {
            FaIcon(if (allExpanded) "fa-solid fa-angles-up" else "fa-solid fa-angles-down", size = "0.72rem")
            Text(if (allExpanded) " Fäll ihop alla" else " Expandera alla")
        }
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem") } }) {
        byPerson.forEach { (userId, personRecs) ->
            val name = listOfNotNull(personRecs.first().firstName, personRecs.first().lastName).joinToString(" ")
                .ifEmpty { personRecs.first().userEmail }
            val initials = name.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
            val totalMin = personRecs.sumOf { it.netMinutes }
            val totalH = totalMin / 60
            val totalM = totalMin % 60
            val totalLabel = "${totalH}h${if (totalM > 0) "${totalM}m" else ""}"
            val unapproved = personRecs.filter { !it.approved }
            val expanded = expandedIds[userId] == true

            Div({ style {
                property("background", "var(--rp-surface)"); property("border-radius", "16px")
                property("overflow", "hidden"); property("border", "1px solid var(--rp-hl-med)")
            } }) {
                // Header row (same as month)
                Div({
                    onClick { expandedIds[userId] = !expanded }
                    style {
                        property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem")
                        property("padding", "1rem 1.25rem"); property("cursor", "pointer")
                        property("background", if (expanded) "var(--rp-surface)" else "var(--rp-surface)")
                    }
                }) {
                    val colorScheme = getEmployeeColorScheme(null, userId)
                    Div({ style {
                        property("width", "36px"); property("height", "36px"); property("border-radius", "50%")
                        property("background", colorScheme.bg); property("display", "flex")
                        property("align-items", "center"); property("justify-content", "center")
                        property("font-weight", "800"); property("font-size", "0.75rem"); property("color", colorScheme.text)
                        property("border", "2px solid ${colorScheme.primary}")
                        property("flex-shrink", "0")
                    } }) { Text(initials.ifEmpty { "?" }) }

                    Div({ style { property("flex", "1"); property("min-width", "0") } }) {
                        Div({ style {
                            property("font-weight", "700"); property("font-size", "0.95rem"); property("color", "var(--rp-text)")
                            property("white-space", "nowrap"); property("overflow", "hidden"); property("text-overflow", "ellipsis")
                        } }) { Text(name) }
                        Div({ style { property("display", "flex"); property("gap", "0.5rem"); property("margin-top", "0.2rem"); property("flex-wrap", "wrap") } }) {
                            Span({ style {
                                property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-pine)")
                                property("background", "var(--rp-info-bg)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                            } }) { Text(totalLabel) }
                            Span({ style {
                                property("font-size", "0.75rem"); property("color", "var(--rp-subtle)")
                                property("background", "var(--rp-overlay)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                            } }) { Text("${personRecs.size} pass") }
                            if (personRecs.all { it.approved }) {
                                Span({ style {
                                    property("font-size", "0.75rem"); property("color", "var(--rp-ok-text)")
                                    property("background", "var(--rp-ok-bg)"); property("padding", "0.15rem 0.5rem"); property("border-radius", "20px")
                                } }) { Text("Alla godkända") }
                            }
                        }
                    }

                    if (unapproved.isNotEmpty()) {
                        Button({
                            onClick { ev ->
                                ev.stopPropagation()
                                weekScope.launch {
                                    unapproved.forEach { r ->
                                        payrollRepo.updateRecord(r.id, UpdatePayrollRecordRequest(approved = true))
                                    }
                                    onReload()
                                }
                            }
                            style {
                                property("padding", "0.4rem 0.75rem"); property("border", "none")
                                property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                                property("border-radius", "8px"); property("font-weight", "700"); property("font-size", "0.75rem")
                                property("cursor", "pointer"); property("white-space", "nowrap")
                            }
                        }) {
                            FaIcon("fa-solid fa-check-double", size = "0.7rem")
                            Text(" Godkänn alla")
                        }
                    }

                    FaIcon(if (expanded) "fa-solid fa-chevron-up" else "fa-solid fa-chevron-down", size = "0.8rem")
                }

                // Expanded: rows sorted by day (Mon–Sun within the week)
                if (expanded) {
                    Div({ style { property("border-top", "1px solid var(--rp-hl-med)") } }) {
                        personRecs.sortedBy { it.day }.forEach { rec ->
                            val dateParts = rec.day.split("-")
                            val dayNum = dateParts[2].toInt()
                            val monthIdx = dateParts[1].toInt() - 1
                            val dayOfWeek = run {
                                val d = parseDate(rec.day)
                                DAY_NAMES_SHORT[(d.getDay() + 6) % 7]
                            }
                            val netH = rec.netMinutes / 60
                            val netM = rec.netMinutes % 60
                            val netLabel = "${netH}h${if (netM > 0) "${netM}m" else ""}"

                            Div({ style {
                                if (isMobile) {
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("gap", "0.5rem")
                                    property("padding", "0.75rem 1rem")
                                } else {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.75rem")
                                    property("padding", "0.75rem 1.25rem")
                                }
                                property("background", if (rec.approved) "var(--rp-ok-bg)" else "transparent")
                                property("border-bottom", "1px solid var(--rp-hl-med)")
                            } }) {
                                if (isMobile) {
                                    // Mobile top bar: Day + Net hours + Status + actions
                                    Div({ style {
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "space-between")
                                        property("width", "100%")
                                    } }) {
                                        Div({ style { property("font-size", "0.82rem"); property("color", "var(--rp-subtle)") } }) {
                                            Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(dayOfWeek) }
                                            Text(" $dayNum ${MONTH_NAMES[monthIdx].take(3)}")
                                        }

                                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem") } }) {
                                            Span({ style {
                                                property("font-weight", "800"); property("font-size", "0.82rem"); property("color", "var(--rp-text)")
                                            } }) { Text(netLabel) }

                                            Span({ style {
                                                property("padding", "0.15rem 0.45rem"); property("border-radius", "20px")
                                                property("font-size", "0.65rem"); property("font-weight", "700")
                                                if (rec.approved) { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)") }
                                                else { property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)") }
                                            } }) { Text(if (rec.approved) "OK" else "Väntar") }

                                            if (rec.approved) {
                                                Button({
                                                    onClick {
                                                        weekScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = false))
                                                            onReload()
                                                        }
                                                    }
                                                    attr("title", "Lås upp för redigering")
                                                    style {
                                                        property("background", "var(--rp-ok-bg)"); property("border", "none")
                                                        property("border-radius", "8px"); property("padding", "0.25rem 0.45rem")
                                                        property("cursor", "pointer"); property("color", "var(--rp-ok-text)")
                                                    }
                                                }) { FaIcon("fa-solid fa-lock", size = "0.72rem") }
                                            } else {
                                                Button({
                                                    onClick { onEditRecord(rec) }
                                                    style {
                                                        property("background", "var(--rp-overlay)"); property("border", "none")
                                                        property("border-radius", "8px"); property("padding", "0.25rem 0.45rem")
                                                        property("cursor", "pointer")
                                                    }
                                                }) { FaIcon("fa-solid fa-pen", size = "0.72rem") }

                                                Button({
                                                    onClick {
                                                        weekScope.launch {
                                                            payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = true))
                                                            onReload()
                                                        }
                                                    }
                                                    style {
                                                        property("background", "var(--rp-ok-bg)"); property("border", "none")
                                                        property("border-radius", "8px"); property("padding", "0.25rem 0.45rem")
                                                        property("cursor", "pointer"); property("color", "var(--rp-ok-text)")
                                                    }
                                                }) { FaIcon("fa-solid fa-check", size = "0.72rem") }
                                            }
                                        }
                                    }

                                    // Timeline bar underneath with full width
                                    Div({ style { property("width", "100%"); property("padding-top", "0.2rem") } }) {
                                        PayrollTimelineBar(
                                            rec = rec, drag = drag,
                                            onDragStart = onDragStart,
                                            onDragUpdate = onDragUpdate,
                                            onDragEnd = onDragEnd,
                                            isMobile = true
                                        )
                                    }
                                } else {
                                    // Day label
                                    Div({ style {
                                        property("width", "90px"); property("flex-shrink", "0")
                                        property("font-size", "0.78rem"); property("color", "var(--rp-subtle)")
                                    } }) {
                                        Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(dayOfWeek) }
                                        Text(" $dayNum ${MONTH_NAMES[monthIdx].take(3)}")
                                    }

                                    // Timeline bar
                                    Div({ style { property("flex", "1"); property("min-width", "0") } }) {
                                        PayrollTimelineBar(
                                            rec = rec, drag = drag,
                                            onDragStart = onDragStart,
                                            onDragUpdate = onDragUpdate,
                                            onDragEnd = onDragEnd,
                                            isMobile = false
                                        )
                                    }

                                    // Net hours
                                    Span({ style {
                                        property("width", "45px"); property("text-align", "right")
                                        property("font-weight", "700"); property("font-size", "0.8rem"); property("color", "var(--rp-text)")
                                        property("flex-shrink", "0")
                                    } }) { Text(netLabel) }

                                    // Status chip
                                    Span({ style {
                                        property("padding", "0.2rem 0.5rem"); property("border-radius", "20px")
                                        property("font-size", "0.68rem"); property("font-weight", "700"); property("flex-shrink", "0")
                                        if (rec.approved) { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)") }
                                        else { property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)") }
                                    } }) { Text(if (rec.approved) "OK" else "Väntar") }

                                    // Edit or unlock button
                                    if (rec.approved) {
                                        Button({
                                            onClick {
                                                weekScope.launch {
                                                    payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = false))
                                                    onReload()
                                                }
                                            }
                                            attr("title", "Lås upp för redigering")
                                            style {
                                                property("background", "var(--rp-ok-bg)"); property("border", "none")
                                                property("border-radius", "8px"); property("padding", "0.3rem 0.5rem")
                                                property("cursor", "pointer"); property("color", "var(--rp-ok-text)"); property("flex-shrink", "0")
                                            }
                                        }) { FaIcon("fa-solid fa-lock", size = "0.72rem") }
                                    } else {
                                        Button({
                                            onClick { onEditRecord(rec) }
                                            style {
                                                property("background", "var(--rp-overlay)"); property("border", "none")
                                                property("border-radius", "8px"); property("padding", "0.3rem 0.5rem")
                                                property("cursor", "pointer"); property("flex-shrink", "0")
                                            }
                                        }) { FaIcon("fa-solid fa-pen", size = "0.72rem") }

                                        Button({
                                            onClick {
                                                weekScope.launch {
                                                    payrollRepo.updateRecord(rec.id, UpdatePayrollRecordRequest(approved = true))
                                                    onReload()
                                                }
                                            }
                                            style {
                                                property("background", "var(--rp-ok-bg)"); property("border", "none")
                                                property("border-radius", "8px"); property("padding", "0.3rem 0.5rem")
                                                property("cursor", "pointer"); property("color", "var(--rp-ok-text)"); property("flex-shrink", "0")
                                            }
                                        }) { FaIcon("fa-solid fa-check", size = "0.72rem") }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
